import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { Clock, MapPin, Users, Plus, Edit2, Trash2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ShiftManagement({ user, onLogout }) {
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    start_time: '',
    end_time: '',
    break_duration: 60,
    working_hours: 8,
    days_of_week: [],
    is_active: true
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchShifts();
    fetchLocations();
    fetchDashboardStats();
  }, []);

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(response.data);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const shiftData = {
        ...formData,
        id: editingShift ? editingShift.id : `SHIFT${Date.now()}`,
      };

      if (editingShift) {
        await axios.put(`${API}/shifts/${editingShift.id}`, shiftData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Shift updated successfully!');
      } else {
        await axios.post(`${API}/shifts`, shiftData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Shift created successfully!');
      }

      setShowForm(false);
      setEditingShift(null);
      setFormData({
        name: '',
        location_id: '',
        start_time: '',
        end_time: '',
        break_duration: 60,
        working_hours: 8,
        days_of_week: [],
        is_active: true
      });
      fetchShifts();
    } catch (error) {
      alert('Failed to save shift: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      location_id: shift.location_id,
      start_time: shift.start_time,
      end_time: shift.end_time,
      break_duration: shift.break_duration || 60,
      working_hours: shift.working_hours || 8,
      days_of_week: shift.days_of_week || [],
      is_active: shift.is_active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/shifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Shift deleted successfully!');
      fetchShifts();
    } catch (error) {
      alert('Failed to delete shift');
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : locationId;
  };

  // Filter shifts - check both location_id and branch_id since they may use different ID formats
  // Also match by location name for better compatibility
  const selectedLocationData = locations.find(l => l.id === selectedLocation);
  const filteredShifts = selectedLocation === 'all' 
    ? shifts 
    : shifts.filter(s => 
        s.location_id === selectedLocation || 
        s.branch_id === selectedLocation ||
        (selectedLocationData && (
          s.location_name === selectedLocationData.name ||
          s.branch === selectedLocationData.name
        ))
      );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
            Shift Management
          </h1>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingShift(null);
              setFormData({
                name: '',
                location_id: '',
                start_time: '',
                end_time: '',
                break_duration: 60,
                working_hours: 8,
                days_of_week: [],
                is_active: true
              });
            }}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            {showForm ? 'Cancel' : 'Add Shift'}
          </button>
        </div>

        {/* Shift Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Shift Management Overview"
            icon="🕐"
            bgColor="#607d8b"
            stats={[
              {
                label: 'Total Shifts',
                value: shifts.length,
                subtitle: 'Configured shifts',
                color: '#607d8b'
              },
              {
                label: 'Active Locations',
                value: locations.length,
                subtitle: 'With shifts',
                color: '#2b3380'
              },
              {
                label: 'Active Shifts',
                value: shifts.filter(s => s.is_active).length,
                subtitle: 'Currently active',
                color: '#4caf50'
              },
              {
                label: 'Employees on Shift',
                value: dashboardStats.employees_on_shift || 0,
                subtitle: 'Currently working',
                color: '#ff9800'
              }
            ]}
          />
        )}

        {/* Location Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-4">
            <MapPin className="w-5 h-5" style={{color: '#2b3380'}} />
            <span className="font-medium text-gray-700">Filter by Location:</span>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">
              Showing {filteredShifts.length} shift{filteredShifts.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
              {editingShift ? 'Edit Shift' : 'Add New Shift'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Shift Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Morning Shift, Night Shift"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Location *</label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Start Time *</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">End Time *</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Break Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.break_duration}
                    onChange={(e) => setFormData({...formData, break_duration: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Working Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.working_hours}
                    onChange={(e) => setFormData({...formData, working_hours: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                    max="24"
                  />
                </div>
              </div>

              {/* Days of Week */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Working Days</label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        formData.days_of_week.includes(day)
                          ? 'text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      style={formData.days_of_week.includes(day) ? {backgroundColor: '#2b3380'} : {}}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Toggle */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium">Active Shift</span>
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg text-white"
                  style={{backgroundColor: '#2b3380'}}
                >
                  {editingShift ? 'Update Shift' : 'Create Shift'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingShift(null);
                  }}
                  className="px-6 py-2 rounded-lg border"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Shifts List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead style={{backgroundColor: '#f5f7fa'}}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Shift Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Working Days</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShifts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No shifts configured yet</p>
                    <p className="text-sm">Create your first shift to get started</p>
                  </td>
                </tr>
              ) : (
                filteredShifts.map((shift) => (
                  <tr key={shift.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5" style={{color: '#2b3380'}} />
                        <span className="font-medium">{shift.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{getLocationName(shift.location_id)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div>{shift.start_time} - {shift.end_time}</div>
                        <div className="text-gray-500">
                          {shift.working_hours}hrs (Break: {shift.break_duration}min)
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">
                        {shift.days_of_week && shift.days_of_week.length > 0
                          ? shift.days_of_week.map(d => d.substring(0, 3)).join(', ')
                          : 'All days'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        shift.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {shift.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(shift)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(shift.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default ShiftManagement;
